import './index-f674799c.js';
import { toRefs, watch, computed, resolveComponent, resolveDirective, openBlock, createElementBlock, normalizeClass, Fragment, withDirectives, createElementVNode, createVNode, createCommentVNode, vShow, toDisplayString } from 'vue';
import { useField } from 'vee-validate';
import { u as useToggler } from './useToggler-e9ff86c0.js';
import moment from 'moment';
import Calendar from 'primevue/calendar';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

library.add(faQuestionCircle);
var script = {
  name: 'form-control-datetime',
  props: {
    name: {
      required: true,
      type: String
    },
    code: {
      required: false,
      type: String,
      default: ''
    },
    label: {
      required: false,
      type: String,
      default: ''
    },
    required: {
      required: false,
      type: Boolean,
      default: false
    },
    placeholder: {
      required: false,
      type: String,
      default: 'Zeitraum wählen'
    },
    defaultvalue: {
      required: false,
      type: String,
      default: ''
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: ''
    },
    error: {
      required: false,
      type: String,
      default: ''
    },
    serverError: {
      required: false,
      type: String,
      default: ''
    },
    help: {
      required: false,
      type: String,
      default: ''
    },
    hidden: {
      required: false,
      type: Boolean,
      default: false
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-form-attr'
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true
    },
    readonly: {
      required: false,
      type: Boolean
    },
    min: {
      required: false,
      type: String
    },
    max: {
      required: false,
      type: String
    },
    showTime: {
      required: false,
      type: Boolean
    },
    dateFormat: {
      required: false,
      type: String
    },
    selectionMode: {
      required: false,
      type: String
    },
    showButtonBar: {
      required: false,
      type: Boolean,
      default: null
    },
    selectOtherMonths: {
      required: false,
      type: Boolean,
      default: true
    }
  },
  emits: ['validate'],
  setup: function setup(props, context) {
    var _toRefs = toRefs(props),
        name = _toRefs.name,
        code = _toRefs.code,
        label = _toRefs.label,
        min = _toRefs.min,
        max = _toRefs.max,
        required = _toRefs.required,
        defaultvalue = _toRefs.defaultvalue,
        error = _toRefs.error,
        serverError = _toRefs.serverError,
        additionalLabelClasses = _toRefs.additionalLabelClasses,
        shouldcache = _toRefs.shouldcache,
        prefixId = _toRefs.prefixId;

    var _useToggler = useToggler(),
        shouldShowHelp = _useToggler.toggleValue,
        toggleHelpText = _useToggler.toggle;

    var minDate = min.value ? new Date(min.value) : undefined;
    var maxDate = max.value ? new Date(max.value) : undefined; //+---------------------+
    //|    Other functions  |
    //+---------------------+

    function validate(value) {
      // vee-validate's composition API
      var finalvalue = value ? value : '';
      var returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: true,
        name: name.value,
        code: code.value
      };

      if (required.value) {
        var finaldate = moment(finalvalue);
        var validated = true;

        if (!finaldate.isValid()) {
          validated = false;
        }

        if (min.value) {
          var mindate = moment(min.value); // check that selected date is after provided min date

          if (mindate.isValid() && !finaldate.isSameOrAfter(mindate, 'day')) {
            validated = false;
          }
        }

        if (max.value) {
          var maxdate = moment(max.value); // check that selected date is before provided max date

          if (maxdate.isValid() && !finaldate.isSameOrBefore(maxdate, 'day')) {
            validated = false;
          }
        }

        returnValue.validated = validated;
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, String(returnValue.value));
      } // Emit event


      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }

    var _useField = useField(name, validate, {
      initialValue: defaultvalue.value ? new Date(defaultvalue.value) : null
    }),
        errorMessage = _useField.errorMessage,
        value = _useField.value;

    watch(defaultvalue, function (nV) {
      value.value = nV ? new Date(nV) : null;
    });

    function setValue(date) {
      validate(date);
      value.value = new Date(date);
    }

    context.expose({
      setValue: setValue,
      value: value
    }); //+---------------------+
    //|    computed()       |
    //+---------------------+

    var hasError = computed(function () {
      return errorMessage.value != undefined;
    });
    var hasServerError = computed(function () {
      return serverError.value != undefined && serverError.value !== '';
    });
    var wrapperClassName = computed(function () {
      return {
        'form-group': true,
        'has-error': hasError.value || hasServerError.value
      };
    });
    var labelClassName = computed(function () {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    var inputClassName = computed(function () {
      return "form-control  ".concat(hasError.value || hasServerError.value ? 'is-invalid' : '');
    });
    var labelText = computed(function () {
      return label.value + (required.value ? '*' : '');
    });
    var idInput = computed(function () {
      return prefixId.value + '-text-' + name.value;
    });
    var idErrorText = computed(function () {
      return prefixId.value + '-text-' + name.value + '-error';
    }); //+---------------------+
    //|  Lifecycle Hooks    |
    //+---------------------+
    //+---------------------+
    //|    return data      |
    //+---------------------+

    return {
      // Computed
      hasError: hasError,
      hasServerError: hasServerError,
      wrapperClassName: wrapperClassName,
      labelClassName: labelClassName,
      inputClassName: inputClassName,
      idInput: idInput,
      idErrorText: idErrorText,
      labelText: labelText,
      // Methods
      toggleHelpText: toggleHelpText,
      // Variables
      shouldShowHelp: shouldShowHelp,
      errorMessage: errorMessage,
      minDate: minDate,
      maxDate: maxDate,
      value: value
    };
  },
  components: {
    Calendar: Calendar
  }
};

var _hoisted_1 = ["for", "aria-label"];
var _hoisted_2 = {
  class: "alert alert-info alert-dismissible mb-2 form-info-message",
  role: "alert"
};
var _hoisted_3 = ["id"];
var _hoisted_4 = ["id"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_font_awesome_icon = resolveComponent("font-awesome-icon");

  var _component_Calendar = resolveComponent("Calendar");

  var _directive_dompurify_html = resolveDirective("dompurify-html");

  return $props.visibleIfResult ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass($setup.wrapperClassName),
    id: "wdw-library-form-control-datetime"
  }, [!$props.hidden ? (openBlock(), createElementBlock(Fragment, {
    key: 0
  }, [withDirectives(createElementVNode("label", {
    class: normalizeClass($setup.labelClassName),
    for: $setup.idInput,
    "aria-label": $props.label
  }, null, 10, _hoisted_1), [[_directive_dompurify_html, $setup.labelText]]), $props.help ? (openBlock(), createElementBlock("span", {
    key: 0,
    class: "help-trigger",
    title: "Hilfe anzeigen",
    onClick: _cache[0] || (_cache[0] = function () {
      return $setup.toggleHelpText && $setup.toggleHelpText.apply($setup, arguments);
    })
  }, [createVNode(_component_font_awesome_icon, {
    icon: "question-circle",
    size: "lg"
  })])) : createCommentVNode("", true), withDirectives(createElementVNode("div", _hoisted_2, [withDirectives(createElementVNode("span", null, null, 512), [[_directive_dompurify_html, $props.help]]), createElementVNode("button", {
    type: "button",
    class: "btn-close",
    "aria-label": "Close",
    onClick: _cache[1] || (_cache[1] = function () {
      return $setup.toggleHelpText && $setup.toggleHelpText.apply($setup, arguments);
    })
  })], 512), [[vShow, $setup.shouldShowHelp]])], 64)) : createCommentVNode("", true), createElementVNode("div", null, [createVNode(_component_Calendar, {
    class: "datetime-input",
    id: $setup.idInput,
    "data-wdw-code": $props.code,
    "data-vv-as": $setup.labelText,
    name: $props.name,
    dateFormat: $props.dateFormat,
    required: $props.required,
    placeholder: $props.placeholder,
    inputClass: $setup.inputClassName,
    modelValue: $setup.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = function ($event) {
      return $setup.value = $event;
    }),
    tabindex: "0",
    "aria-invalid": $setup.hasError || $setup.hasServerError,
    "aria-describedby": $setup.idErrorText,
    hidden: $props.hidden,
    disabled: $props.readonly,
    showTime: $props.showTime,
    hourFormat: "24",
    minDate: $setup.minDate,
    maxDate: $setup.maxDate,
    showIcon: true,
    selectionMode: $props.selectionMode,
    selectOtherMonths: $props.selectOtherMonths,
    showButtonBar: $props.showButtonBar,
    manualInput: true
  }, null, 8, ["id", "data-wdw-code", "data-vv-as", "name", "dateFormat", "required", "placeholder", "inputClass", "modelValue", "aria-invalid", "aria-describedby", "hidden", "disabled", "showTime", "minDate", "maxDate", "selectionMode", "selectOtherMonths", "showButtonBar"])]), !$props.hidden ? (openBlock(), createElementBlock(Fragment, {
    key: 1
  }, [withDirectives(createElementVNode("span", {
    id: $setup.idErrorText,
    class: "text-danger form-error-message",
    "aria-atomic": "true"
  }, toDisplayString($props.error), 9, _hoisted_3), [[vShow, $setup.hasError]]), withDirectives(createElementVNode("span", {
    id: $setup.idErrorText,
    class: "text-danger form-error-message",
    "aria-atomic": "true"
  }, toDisplayString($props.serverError), 9, _hoisted_4), [[vShow, $setup.hasServerError]])], 64)) : createCommentVNode("", true)], 2)) : createCommentVNode("", true);
}

script.render = render;
script.__scopeId = "data-v-60a98d42";

export { script as s };
